"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.InterviewParadata = void 0;
const ConstructorUtils_1 = require("../../../utils/ConstructorUtils");
const SurveyObjectUnserializer_1 = require("../SurveyObjectUnserializer");
const ParamsValidatorUtils_1 = require("../../../utils/ParamsValidatorUtils");
const Result_type_1 = require("../../../types/Result.type");
/*
We need to set these arrays to be able to split attributes and custom attributes during initialization.
Using keyof do not work because constructor is run-time and keyof is typescript only.
*/
const interviewParadataAttributes = [
    'startedAt',
    'updatedAt',
    'completedAt',
    'source',
    'personsRandomSequence',
    'languages',
    'browsers',
    'sections'
];
const interviewParadataAttributesWithComposedAttributes = [...interviewParadataAttributes];
/**
 * Represents metadata associated with an interview (paradata).
 * See https://en.wikipedia.org/wiki/Paradata
 *
 * @class
 * @description
 * The InterviewParadata class manages paradata related to an interview.
 * It is typically created as part of the Interview object initialization process.
 *
 * The InterviewParadata instance stores various metadata such as timestamps,
 * browser information, and section data.
 *
 * This paradata is crucial for understanding the context and progress of an interview,
 * and may be used in the auditing process handled by InterviewAudited.
 */
class InterviewParadata {
    // Use InterviewParadataUnserializer create function to generate/validate InterviewParadata object from json data
    constructor(params) {
        this._attributes = {};
        const { attributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes(params, interviewParadataAttributes, interviewParadataAttributesWithComposedAttributes);
        this._attributes = attributes;
    }
    get attributes() {
        return this._attributes;
    }
    // Getters and setters for the attributes dictionary:
    get startedAt() {
        return this._attributes.startedAt;
    }
    set startedAt(value) {
        this._attributes.startedAt = value;
    }
    get updatedAt() {
        return this._attributes.updatedAt;
    }
    set updatedAt(value) {
        this._attributes.updatedAt = value;
    }
    get completedAt() {
        return this._attributes.completedAt;
    }
    set completedAt(value) {
        this._attributes.completedAt = value;
    }
    get source() {
        return this._attributes.source;
    }
    set source(value) {
        this._attributes.source = value;
    }
    get personsRandomSequence() {
        return this._attributes.personsRandomSequence;
    }
    set personsRandomSequence(value) {
        this._attributes.personsRandomSequence = value;
    }
    get languages() {
        return this._attributes.languages || [];
    }
    set languages(value) {
        this._attributes.languages = value || [];
    }
    get browsers() {
        return this._attributes.browsers || [];
    }
    set browsers(value) {
        this._attributes.browsers = value || [];
    }
    get sections() {
        return this._attributes.sections || {};
    }
    set sections(value) {
        this._attributes.sections = value || {};
    }
    /**
     * Creates an InterviewParadata object from sanitized parameters
     * @param {ExtendedInterviewParadataAttributes | SerializedExtendedInterviewParadataAttributes} params - Sanitized interview paradata parameters
     * @param {SurveyObjectsRegistry} _surveyObjectsRegistry - unused since InterviewParadata is not composed of other objects, but unserializer will send the optional param.
     * @returns {InterviewParadata} New InterviewParadata instance
     */
    static unserialize(params, _surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new InterviewParadata(flattenedParams);
    }
    /**
     * Factory that validates input and creates a InterviewParadata object
     * @param {Object} dirtyParams - Raw input parameters to validate
     * @returns {Result<InterviewParadata>} Either a valid InterviewParadata object or validation errors
     */
    static create(dirtyParams) {
        const errors = InterviewParadata.validateParams(dirtyParams);
        const interviewParadata = errors.length === 0 ? new InterviewParadata(dirtyParams) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(interviewParadata);
    }
}
exports.InterviewParadata = InterviewParadata;
InterviewParadata._confidentialAttributes = [
    // do not export these attributes except for admins
    'startedAt',
    'updatedAt',
    'completedAt',
    'personsRandomSequence',
    'languages', // TODO: automatically select the most used language during the interview (longest duration)
    'browsers', // TODO: only some of this metadata should be exported, like the device and/or os and/or browser name
    'sections'
];
/**
 * Validate the interview paradata params.
 * @param dirtyParams the json data
 * @param displayName the name of the object for error messages
 * @returns a list of errors if any, or an empty list if the params are valid
};
 */
InterviewParadata.validateParams = function (dirtyParams, displayName = 'InterviewParadata') {
    const errors = [];
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
    // TODO: also check if start/end timestamps are inside the survey period:
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('startedAt', dirtyParams.startedAt, displayName));
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('updatedAt', dirtyParams.updatedAt, displayName));
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('completedAt', dirtyParams.completedAt, displayName));
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('source', dirtyParams.source, displayName));
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArrayOfStrings('personsRandomSequence', dirtyParams.personsRandomSequence, displayName));
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArray('languages', dirtyParams.languages, displayName));
    if (dirtyParams.languages && Array.isArray(dirtyParams.languages)) {
        for (let i = 0; i < dirtyParams.languages.length; i++) {
            const language = dirtyParams.languages[i];
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`languages.[${i}].language`, language.language, displayName));
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger(`languages.[${i}].startTimestamp`, language.startTimestamp, displayName));
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger(`languages.[${i}].endTimestamp`, language.endTimestamp, displayName));
        }
    }
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isArray('browsers', dirtyParams.browsers, displayName));
    if (dirtyParams.browsers && Array.isArray(dirtyParams.browsers)) {
        for (let i = 0; i < dirtyParams.browsers.length; i++) {
            const browser = dirtyParams.browsers[i];
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}]._ua`, browser._ua, displayName));
            if (browser.browser) {
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].browser.name`, browser.browser.name, displayName));
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].browser.version`, browser.browser.version, displayName));
            }
            if (browser.engine) {
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].engine.name`, browser.engine.name, displayName));
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].engine.version`, browser.engine.version, displayName));
            }
            if (browser.os) {
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].os.name`, browser.os.name, displayName));
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].os.version`, browser.os.version, displayName));
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].os.versionName`, browser.os.versionName, displayName));
            }
            if (browser.platform) {
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].platform.model`, browser.platform.model, displayName));
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].platform.type`, browser.platform.type, displayName));
                errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString(`browsers.[${i}].platform.vendor`, browser.platform.vendor, displayName));
            }
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger(`browsers.[${i}].startTimestamp`, browser.startTimestamp, displayName));
            errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger(`browsers.[${i}].endTimestamp`, browser.endTimestamp, displayName));
        }
    }
    errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('sections', dirtyParams.sections, displayName));
    // TODO: validate the section metadata object when we will have implemented the format
    return errors;
};
//# sourceMappingURL=InterviewParadata.js.map